import React, { FC, MouseEvent, KeyboardEvent } from 'react';
import { useTranslations } from '@lang';
import { Filters } from '@common/features/JourneyPicker/Filters';
import { useRouter } from 'shared-routing';
import { Routes } from '@common/constants';
import { useChildAges } from '@common/components/GuestPicker';
import { FiltersWrapper } from './DatePicker.styles';
import { DatePickerProps } from './DatePicker.types';
import { useModuleStatuses } from '@features/properties';
import { useRoomSettings } from '@common/features/roomSettings';
import { updateRoomsAfterCountChange } from '@common/features/JourneyPicker/utils/updateRooms.utils';

export const DatePicker: FC<DatePickerProps> = ({
  journeyParams,
  property,
  isFiltersVisible,
  changeJourneyParam,
  isHomepage,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const { fromDate, toDate, adults, children, childAges = {}, rooms } = journeyParams;
  const { isTablesIntegrated, isSpaIntegrated, isRetailEnabled } = property;
  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const { data: roomSettings } = useRoomSettings();
  const { setSubmitted, onCloseGuestPicker, childAgesProps } = useChildAges({
    childAges,
    childCount: children,
    dispatchAction: changeJourneyParam,
  });

  const handleFiltersApply = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSubmitted();
    if (isRoomsEnabled) {
      if (!childAgesProps) {
        router.push({ pathname: Routes.ROOM_LIST });
        return;
      }

      if (!childAgesProps.errors.length) {
        router.push({ pathname: Routes.ROOM_LIST });
      }

      return;
    }
    if (isSpaIntegrated) {
      router.push({ pathname: Routes.SPA_HOME });
      return;
    }
    if (isTablesIntegrated) {
      router.push({ pathname: Routes.TABLES_HOME });
      return;
    }
    if (isRetailEnabled) {
      router.push({ pathname: Routes.PRODUCT_LIST });
      return;
    }
    return;
  };

  const handleDateChange = ({ startDate, endDate = null }) => {
    changeJourneyParam('fromDate', startDate || fromDate);
    changeJourneyParam('spaDate', startDate || fromDate);
    changeJourneyParam('toDate', endDate || toDate);
  };

  const handleGuestCountChange = (guest, count, roomNumber) => {
    changeJourneyParam(
      guest,
      ((rooms || []).reduce((acc, room) => acc + room[guest], 0) ?? guest) + count
    );
    if (roomNumber && rooms) {
      const updatedRooms = rooms.map((room) =>
        room.roomNumber === roomNumber
          ? {
              ...room,
              [guest]: room[guest] + count,
            }
          : room
      );
      changeJourneyParam('rooms', updatedRooms);
    }
  };

  const handleRoomCountChange = (roomNumber?: number) => {
    const { updatedRooms } = updateRoomsAfterCountChange(
      rooms || [],
      roomSettings?.data.defaultParty,
      roomNumber
    );

    changeJourneyParam('rooms', updatedRooms);
    changeJourneyParam(
      'adults',
      (updatedRooms || []).reduce((acc, room) => acc + room.adults, 0) ?? adults
    );
    changeJourneyParam(
      'children',
      (updatedRooms || []).reduce((acc, room) => acc + room.children, 0) ?? children
    );
  };

  return isFiltersVisible ? (
    <FiltersWrapper>
      <Filters
        isSingle={!isRoomsEnabled}
        isPrimary
        showGuestsPicker
        fromDate={fromDate}
        toDate={toDate}
        numberOfAdults={adults}
        numberOfChildren={children}
        onDateChange={handleDateChange}
        onGuestCountChange={handleGuestCountChange}
        onFiltersApply={handleFiltersApply}
        submitLabel={t('common.search')}
        childAgesProps={childAgesProps}
        onCloseGuestPicker={onCloseGuestPicker}
        isHomepage={isHomepage}
        rooms={rooms}
        onRoomCountChange={handleRoomCountChange}
      />
    </FiltersWrapper>
  ) : null;
};
